import { StrictMode, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import {
  type Board,
  type Cell,
  addMine,
  cleanCells,
  decodeBoard,
  encodeBoard,
  forget,
  hashString,
  layout,
  load,
  loadMine,
  needed,
  newSeed,
  removeMine,
  save,
  winning,
} from './lib';
import { EXAMPLES } from './examples';
import { type Lang, LangContext, STRINGS, detectLang, rememberLang, useT } from './i18n';

type Route =
  | { name: 'home' }
  | { name: 'new' }
  | { name: 'edit'; board: Board; data: string }
  | { name: 'play'; board: Board; data: string }
  | { name: 'bad' };

const DRAFT = 'bingo:draft';
const EMPTY: Board = { title: '', size: 4, cells: [], free: false, shuffle: true };

function parseRoute(): Route {
  const h = window.location.hash.replace(/^#/, '');
  if (h === 'new') return { name: 'new' };
  const m = /^(play|edit)=(.+)$/.exec(h);
  if (!m) return { name: 'home' };
  const board = decodeBoard(m[2]);
  if (!board) return { name: 'bad' };
  return m[1] === 'play' ? { name: 'play', board, data: m[2] } : { name: 'edit', board, data: m[2] };
}

function playUrl(data: string, lang: Lang): string {
  return `${location.origin}${location.pathname}?lang=${lang}#play=${data}`;
}

function cellFont(text: string): string {
  const len = Math.max(text.length, 1);
  const longest = text.split(/\s+/).reduce((m, w) => Math.max(m, w.length), 1);
  const size = Math.min(20, 108 / Math.sqrt(len), 88 / (longest * 0.6));
  return `${Math.max(6, size).toFixed(2)}cqi`;
}

function titleFont(t: string): string {
  const l = t.length;
  return `${l <= 10 ? 12 : l <= 18 ? 9.5 : l <= 30 ? 7 : l <= 50 ? 5.4 : 4.4}cqi`;
}

function dabStyle(i: number): CSSProperties {
  return {
    '--rot': `${((i * 47) % 40) - 20}deg`,
    '--dx': `${((i * 13) % 7) - 3}%`,
    '--dy': `${((i * 29) % 7) - 3}%`,
  } as CSSProperties;
}

function Card({
  board,
  cells,
  marks,
  wins,
  onToggle,
  small,
}: {
  board: Board;
  cells: Cell[];
  marks?: boolean[];
  wins?: Set<number>;
  onToggle?: (i: number) => void;
  small?: boolean;
}) {
  const { t } = useT();
  const title = board.title || t.defaultTitle;
  return (
    <div className={`card${small ? ' card-small' : ''}`}>
      <div className="card-title" style={{ fontSize: titleFont(title) }}>
        {title}
      </div>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${board.size}, minmax(0, 1fr))` }}>
        {cells.map((text, i) => {
          const isFree = text === null;
          const empty = text === '';
          const marked = isFree || !!marks?.[i];
          const cls = ['cell', marked && 'is-marked', wins?.has(i) && 'is-win', isFree && 'is-free', empty && 'is-empty']
            .filter(Boolean)
            .join(' ');
          const content = (
            <>
              <span className="cell-text" style={{ fontSize: cellFont(isFree ? t.freeCell : text || '…') }}>
                {isFree ? (
                  <>
                    <span className="star">★</span>
                    <br />
                    {t.freeCell}
                  </>
                ) : (
                  text || '…'
                )}
              </span>
              {marked && <span className="dab" style={dabStyle(i)} aria-hidden="true" />}
            </>
          );
          return onToggle && !isFree && !empty ? (
            <button key={i} type="button" className={cls} aria-pressed={marked} onClick={() => onToggle(i)}>
              {content}
            </button>
          ) : (
            <div key={i} className={cls}>
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const COLORS = ['#e5432f', '#f5b700', '#2f6fe5', '#1d9a5b', '#171512', '#ff8ac2'];

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 150 }, (_, i) => ({
        left: Math.random() * 100,
        x: (Math.random() - 0.5) * 260,
        r: (Math.random() - 0.5) * 1440,
        d: 2.2 + Math.random() * 1.8,
        delay: Math.random() * 0.6,
        w: 6 + Math.random() * 6,
        c: COLORS[i % COLORS.length],
      })),
    []
  );
  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <i
          key={i}
          style={
            {
              left: `${p.left}%`,
              width: p.w,
              height: p.w * 1.6,
              background: p.c,
              '--x': `${p.x}px`,
              '--r': `${p.r}deg`,
              '--d': `${p.d}s`,
              '--delay': `${p.delay}s`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

function ShareBox({ board, onClose }: { board: Board; onClose: () => void }) {
  const { t, lang } = useT();
  const url = playUrl(encodeBoard(board), lang);
  const [copied, setCopied] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const canShare = typeof navigator.share === 'function';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    input.current?.select();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      input.current?.select();
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="share-title">{t.shareTitle}</h2>
        <p>
          {board.shuffle ? t.shareShuffle : t.shareSame} {t.shareInLink}
        </p>
        <div className="share-row">
          <input ref={input} readOnly value={url} aria-label={t.linkLabel} onFocus={(e) => e.target.select()} />
          <button type="button" className="btn btn-primary" onClick={copy}>
            {copied ? t.copied : t.copy}
          </button>
        </div>
        <div className="row">
          {canShare && (
            <button
              type="button"
              className="btn"
              onClick={() => navigator.share({ title: board.title || t.defaultTitle, url }).catch(() => undefined)}
            >
              {t.shareNative}
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
}

function Home() {
  const { t, lang } = useT();
  const [mine, setMine] = useState(loadMine);
  const examples = EXAMPLES[lang];
  const hero = examples[0];
  const heroData = encodeBoard(hero.board);
  const heroCells = layout(hero.board, 0);
  const heroMarks = heroCells.map((_, i) => hero.sample.includes(i));

  return (
    <main className="home">
      <section className="hero">
        <div className="hero-text">
          <h1>
            {t.heroA} <em>{t.heroB}</em>.
          </h1>
          <p className="lead">{t.lead}</p>
          <div className="row">
            <a className="btn btn-primary btn-big" href="#new">
              {t.create}
            </a>
            <a className="btn btn-big" href={`#play=${heroData}`}>
              {t.playHero}
            </a>
          </div>
        </div>
        <a className="hero-card" href={`#play=${heroData}`} aria-label={t.playHeroLabel}>
          <Card board={hero.board} cells={heroCells} marks={heroMarks} />
        </a>
      </section>

      {mine.length > 0 && (
        <section className="section">
          <h2>{t.recent}</h2>
          <ul className="mine">
            {mine.map((m) => (
              <li key={m.data}>
                <a className="mine-title" href={`#play=${m.data}`}>
                  {m.title || t.untitled}
                </a>
                <span className="mine-actions">
                  <a className="btn btn-small" href={`#play=${m.data}`}>
                    {t.play}
                  </a>
                  <a className="btn btn-small" href={`#edit=${m.data}`}>
                    {t.edit}
                  </a>
                  <button
                    type="button"
                    className="btn btn-small btn-ghost"
                    onClick={() => setMine(removeMine(m.data))}
                    aria-label={t.removeLabel(m.title || t.untitled)}
                  >
                    {t.remove}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="section">
        <h2>{t.inspiration}</h2>
        <div className="examples">
          {examples.map((ex) => {
            const data = encodeBoard(ex.board);
            return (
              <article className="example" key={ex.board.title}>
                <a className="example-card" href={`#play=${data}`} aria-label={t.playLabel(ex.board.title)}>
                  <Card board={ex.board} cells={layout(ex.board, 0)} small />
                </a>
                <h3>{ex.board.title}</h3>
                <p>{ex.blurb}</p>
                <div className="row">
                  <a className="btn btn-small btn-primary" href={`#play=${data}`}>
                    {t.play}
                  </a>
                  <a className="btn btn-small" href={`#edit=${data}`}>
                    {t.editOwn}
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="section how">
        <h2>{t.how}</h2>
        <ol>
          {t.steps.map(([head, body]) => (
            <li key={head}>
              <b>{head}</b> {body}
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}

function Editor({ initial }: { initial: Board | null }) {
  const { t } = useT();
  const [start] = useState<Board>(() => {
    if (initial) return initial;
    const draft = load<string | null>(DRAFT, null);
    return (typeof draft === 'string' && decodeBoard(draft)) || EMPTY;
  });
  const [title, setTitle] = useState(start.title);
  const [size, setSize] = useState<Board['size']>(start.size);
  const [text, setText] = useState(start.cells.join('\n'));
  const [free, setFree] = useState(start.free);
  const [shuffle, setShuffle] = useState(start.shuffle);
  const [sharing, setSharing] = useState(false);

  const board = useMemo<Board>(
    () => ({ title: title.trim(), size, cells: cleanCells(text), free, shuffle }),
    [title, size, text, free, shuffle]
  );

  useEffect(() => {
    if (!initial) save(DRAFT, encodeBoard(board));
  }, [initial, board]);

  const need = needed(board);
  const have = board.cells.length;
  const missing = Math.max(0, need - have);
  const ready = missing === 0;
  const odd = size % 2 === 1;

  const status =
    have === 0
      ? t.stEmpty(need)
      : missing > 0
        ? t.stMissing(missing, have, need)
        : have > need
          ? shuffle
            ? t.stMoreShuffle(need, have)
            : t.stMoreFixed(need)
          : t.stExact(need);

  const play = () => {
    const data = encodeBoard(board);
    addMine(data, board.title);
    if (!initial) forget(DRAFT);
    location.hash = `play=${data}`;
  };

  const share = () => {
    addMine(encodeBoard(board), board.title);
    setSharing(true);
  };

  return (
    <main className="editor">
      <section className="editor-form">
        <h1>{initial ? t.editorEdit : t.editorNew}</h1>
        {initial && <p className="lead">{t.editingCopy}</p>}

        <label className="field">
          <span>{t.title}</span>
          <input value={title} maxLength={80} placeholder={t.titlePh} onChange={(e) => setTitle(e.target.value)} />
        </label>

        <div className="field">
          <span id="size-label">{t.size}</span>
          <div className="seg" role="group" aria-labelledby="size-label">
            {([3, 4, 5] as const).map((s) => (
              <button type="button" key={s} aria-pressed={size === s} onClick={() => setSize(s)}>
                {s} × {s}
              </button>
            ))}
          </div>
        </div>

        <label className="field">
          <span>
            {t.cells} <small>{t.cellsHint}</small>
          </span>
          <textarea value={text} rows={12} placeholder={t.cellsPh} onChange={(e) => setText(e.target.value)} />
        </label>
        <p className={`count ${ready ? 'ok' : 'bad'}`} aria-live="polite">
          {status}
        </p>

        <label className="check">
          <input type="checkbox" checked={shuffle} onChange={(e) => setShuffle(e.target.checked)} />
          <span>
            <b>{t.shuffle}</b>
            <small>{t.shuffleHint}</small>
          </span>
        </label>

        <label className={`check${odd ? '' : ' is-disabled'}`}>
          <input type="checkbox" checked={free && odd} disabled={!odd} onChange={(e) => setFree(e.target.checked)} />
          <span>
            <b>{t.free}</b>
            <small>{odd ? t.freeHintOdd : t.freeHintEven}</small>
          </span>
        </label>

        <div className="row">
          <button type="button" className="btn btn-primary btn-big" disabled={!ready} onClick={play}>
            {t.play}
          </button>
          <button type="button" className="btn btn-big" disabled={!ready} onClick={share}>
            {t.share}
          </button>
        </div>
      </section>

      <aside className="preview" aria-label={t.previewLabel}>
        <p className="preview-label">{t.preview}</p>
        <Card board={board} cells={layout(board, 0)} />
      </aside>

      {sharing && <ShareBox board={board} onClose={() => setSharing(false)} />}
    </main>
  );
}

function readMarks(key: string, total: number): boolean[] {
  const v = load<unknown>(key, []);
  return Array.from({ length: total }, (_, i) => Array.isArray(v) && v[i] === true);
}

function Play({ board, data }: { board: Board; data: string }) {
  const { t } = useT();
  const key = hashString(data);
  const seedKey = `bingo:seed:${key}`;
  const total = board.size * board.size;
  const [seed, setSeed] = useState<number>(() => (board.shuffle ? load<number>(seedKey, 0) || newSeed() : 0));
  const marksKey = `bingo:marks:${key}:${seed}`;
  const [marks, setMarks] = useState<boolean[]>(() => readMarks(marksKey, total));
  const [sharing, setSharing] = useState(false);
  const [burst, setBurst] = useState(0);

  const cells = useMemo(() => layout(board, seed), [board, seed]);
  const effective = useMemo(() => cells.map((c, i) => c === null || !!marks[i]), [cells, marks]);
  const wins = useMemo(() => winning(board.size, effective), [board.size, effective]);
  const lastCount = useRef(wins.count);
  const anyMarked = marks.some(Boolean);

  useEffect(() => {
    if (board.shuffle) save(seedKey, seed);
  }, [board.shuffle, seedKey, seed]);
  useEffect(() => save(marksKey, marks), [marksKey, marks]);
  useEffect(() => addMine(data, board.title), [data, board.title]);
  useEffect(() => {
    if (wins.count > lastCount.current) setBurst((b) => b + 1);
    lastCount.current = wins.count;
  }, [wins.count]);

  const toggle = (i: number) =>
    setMarks((m) => {
      const next = [...m];
      next[i] = !next[i];
      return next;
    });

  const newCard = () => {
    forget(marksKey);
    setSeed(newSeed());
    setMarks(Array(total).fill(false));
  };

  return (
    <main className="play">
      <div className="card-wrap">
        <Card board={board} cells={cells} marks={effective} wins={wins.cells} onToggle={toggle} />
        {wins.count > 0 && (
          <div className="stamp" role="status" key={wins.full ? 'full' : wins.count}>
            {wins.full ? t.stampFull : wins.count > 1 ? `BINGO ×${wins.count}` : t.stampBingo}
          </div>
        )}
      </div>

      <div className="actions no-print">
        <button type="button" className="btn btn-primary" onClick={() => setSharing(true)}>
          {t.invite}
        </button>
        {board.shuffle && (
          <button type="button" className="btn" onClick={newCard}>
            {t.newCard}
          </button>
        )}
        <button type="button" className="btn" disabled={!anyMarked} onClick={() => setMarks(Array(total).fill(false))}>
          {t.clear}
        </button>
        <button type="button" className="btn" onClick={() => window.print()}>
          {t.print}
        </button>
        <a className="btn btn-ghost" href={`#edit=${data}`}>
          {t.editCopy}
        </a>
      </div>
      <p className="note no-print">
        {board.shuffle ? t.noteShuffle : t.noteSame} {t.noteMemory}
      </p>

      {burst > 0 && <Confetti key={burst} />}
      {sharing && <ShareBox board={board} onClose={() => setSharing(false)} />}
    </main>
  );
}

function Bad() {
  const { t } = useT();
  return (
    <main className="bad">
      <h1>{t.badTitle}</h1>
      <p className="lead">{t.badLead}</p>
      <div className="row">
        <a className="btn btn-primary" href="#new">
          {t.badCreate}
        </a>
        <a className="btn" href="#">
          {t.home}
        </a>
      </div>
    </main>
  );
}

function App() {
  const [route, setRoute] = useState<Route>(parseRoute);
  const [lang, setLang] = useState<Lang>(detectLang);
  const t = STRINGS[lang];
  const ctx = useMemo(() => ({ lang, t }), [lang, t]);

  useEffect(() => {
    const onHash = () => {
      setRoute(parseRoute());
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    document.title =
      route.name === 'play'
        ? `${route.board.title || t.defaultTitle} — Bingo`
        : route.name === 'new' || route.name === 'edit'
          ? t.docEditor
          : t.docHome;
  }, [route, t]);

  const switchLang = () => {
    const next: Lang = lang === 'cs' ? 'en' : 'cs';
    rememberLang(next);
    setLang(next);
  };

  return (
    <LangContext.Provider value={ctx}>
      <header className="top no-print">
        <a href="#" className="logo">
          BINGO<span>!</span>
        </a>
        <div className="top-actions">
          <button type="button" className="lang" onClick={switchLang} aria-label={t.switchLabel} lang={lang === 'cs' ? 'en' : 'cs'}>
            {t.switchTo}
          </button>
          {route.name !== 'new' && (
            <a className="btn btn-small" href="#new">
              {t.newBingo}
            </a>
          )}
        </div>
      </header>
      {route.name === 'home' && <Home />}
      {route.name === 'new' && <Editor key="new" initial={null} />}
      {route.name === 'edit' && <Editor key={`edit-${route.data}`} initial={route.board} />}
      {route.name === 'play' && <Play key={route.data} board={route.board} data={route.data} />}
      {route.name === 'bad' && <Bad />}
      <footer className="foot no-print">
        {t.footer} <a href="https://www.drobek.app/">drobek.app</a>
      </footer>
    </LangContext.Provider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
